import React from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Shield, CheckSquare, XSquare } from 'lucide-react';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '../../utils/permissions';

const PermissionsPanel = ({
  user,
  currentUserId,
  onPermissionChange,
  onSelectAllInGroup,
  onGrantAll,
  onRevokeAll
}) => {
  const isOwnUser = user.id === currentUserId;

  const getPermissionCount = () => {
    if (!user.permissions) return 0;
    return Object.values(user.permissions).filter(Boolean).length;
  };

  const getGroupPermissionCount = (groupName) => {
    const group = PERMISSION_GROUPS.find(g => g.name === groupName);
    if (!group || !user.permissions) return { granted: 0, total: group?.permissions?.length || 0 };
    const granted = group.permissions.filter(p => user.permissions[p.key]).length;
    return { granted, total: group.permissions.length };
  };

  return (
    <div className="space-y-3 mt-4 pt-4 border-t border-border">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center text-muted-foreground">
          <Shield className="w-4 h-4 mr-2" />
          Permissions ({getPermissionCount()}/{ALL_PERMISSIONS.length})
        </h4>
        {!isOwnUser && (
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={onGrantAll}
              className="text-green-600 border-green-300 hover:bg-green-50" data-testid={`grant-all-${user.id}`}>
              <CheckSquare className="w-4 h-4 mr-1" /> Select All
            </Button>
            <Button variant="outline" size="sm" onClick={onRevokeAll}
              className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`revoke-all-${user.id}`}>
              <XSquare className="w-4 h-4 mr-1" /> Deselect All
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {PERMISSION_GROUPS.map((group) => {
          const { granted, total } = getGroupPermissionCount(group.name);
          return (
            <div key={group.name} className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-sm text-foreground">{group.name}</span>
                  <span className="text-xs text-muted-foreground">({granted}/{total})</span>
                </div>
                {!isOwnUser && (
                  <div className="flex space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => onSelectAllInGroup(group.name, true)}
                      className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50">All</Button>
                    <Button variant="ghost" size="sm" onClick={() => onSelectAllInGroup(group.name, false)}
                      className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">None</Button>
                  </div>
                )}
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {group.permissions.map((permission) => {
                  const isGranted = user.permissions?.[permission.key] ?? false;
                  return (
                    <div key={permission.key}
                      className={`flex items-start space-x-2 p-2 rounded border ${isGranted ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'} ${isOwnUser ? 'opacity-60' : ''}`}>
                      <Checkbox id={`${user.id}-${permission.key}`} checked={isGranted} disabled={isOwnUser}
                        onCheckedChange={(checked) => onPermissionChange(permission.key, checked)}
                        className="mt-0.5" data-testid={`permission-${user.id}-${permission.key}`} />
                      <div className="flex-1 min-w-0">
                        <label htmlFor={`${user.id}-${permission.key}`}
                          className={`text-xs font-medium cursor-pointer block truncate ${isGranted ? 'text-green-800' : 'text-gray-700'}`}>
                          {permission.label}
                        </label>
                        <p className="text-[10px] text-muted-foreground truncate">{permission.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {isOwnUser && (
        <p className="text-xs text-amber-600 mt-3 flex items-center">
          <Shield className="w-3 h-3 mr-1" />
          You cannot modify your own permissions
        </p>
      )}
    </div>
  );
};

export { PermissionsPanel };
